import { snackbar } from "@bcmi-labs/art-ui/components"
import { queryOptions, useQuery } from "@tanstack/react-query"
import { events } from "fetch-event-stream"
import { useCallback } from "react"
import { uniqueBy } from "remeda"

import { useSpace } from "@/api/hooks/useSpace"
import { $provisioningApiClient } from "@/api/provisioning"
import { invalidateCachedQueries } from "@/api/query/invalidateCachedQueries"
import { getXOrganizationHeaders } from "@/api/utils"
import type { components } from "@/types/api/provisioning/schema.gen"

type SSEData = components["schemas"]["v1.ListRes"]

type SSEEventParsedResponse = {
  onboardings: SSEData["onboardings"]
  events: SSEData["onboardings"]
}

const exec = async (spaceId: string, cb: (data: SSEData) => void, signal?: AbortSignal) => {
  const { response } = await $provisioningApiClient.GET("/v1/onboarding/sse", {
    params: { header: getXOrganizationHeaders(spaceId) },
    parseAs: "stream",
    signal
  })

  if (response.ok) {
    // ! Iterators cannot be reconsumed! Use a promise to capture the first event, and keep on listening.
    const firstEventPromise = new Promise<SSEData>(resolve => {
      ;(async () => {
        try {
          for await (const e of events(response)) {
            if (e.data) {
              const parsedData = JSON.parse(e.data) as SSEData
              resolve(parsedData) // Resolve the promise with the first event
              cb(parsedData) // Pass subsequent events to the callback
            }
          }
        } catch (error) {
          console.error("Error processing SSE events:", error)
        } finally {
          resolve({ onboardings: [] }) // Resolve with null if the stream ends or an error occurs
        }
      })()
    })

    // Wait for the first event to be captured
    const firstData = await firstEventPromise
    return firstData
  }

  // Return a default value if the response is not OK
  return { onboardings: [] }
}

export const provisioningSSEQuery = (spaceId: string) =>
  queryOptions({
    queryFn: async ({ client, queryKey, signal }) => {
      const fn = await exec(
        spaceId,
        d => {
          const oldData: SSEEventParsedResponse = client.getQueryData(queryKey) || { onboardings: [], events: [] }

          const provisionedDevice = d.onboardings.find(o => o.provisioned_at)

          // If the new data includes a provisioned_at device, invalidate the device list.
          if (provisionedDevice) {
            invalidateCachedQueries(spaceId, client).invalidateByGroupKind("DEVICE")
            snackbar({ message: `${provisionedDevice.device_name} is now available 🎉`, variant: "success" })
          }

          client.setQueryData(queryKey, {
            // Update the data with the new one (keep fresh data first, then old data so it gets cleaned up)
            onboardings: uniqueBy(d.onboardings.concat(oldData.onboardings), o => o.id)
              // Remove ended/provisioned/deleted devices
              .filter(o => !(o.ended_at || o.provisioned_at || o.deleted_at)),
            // Keep the events in the same order as they come
            events: oldData.events.concat(d.onboardings)
          } satisfies SSEEventParsedResponse)
        },
        signal
      )

      return { onboardings: fn.onboardings, events: fn.onboardings }
    },
    queryKey: ["sseProvision", spaceId],
    staleTime: Infinity // ? Permanent connection for now, invalidate manually,
  })

export const useSSEProvision = () => {
  const { space } = useSpace()
  const data = useQuery(provisioningSSEQuery(space.id))

  /**
   * Get an onboarding by its ID.
   *
   * This function first checks the onboardings list, and if not found,
   * it looks in the events list for an event with a matching ID that has `provisioned_at` set.
   */
  const getOnboarding = useCallback(
    (onboardingId: string) => {
      // Try onboardings first
      const found = data.data?.onboardings.find(o => o.id === onboardingId)
      if (found) return found

      // Fallback: look in events for a matching id with provisioned_at set
      return data.data?.events.find(e => e.id === onboardingId && e.provisioned_at)
    },
    [data.data?.events, data.data?.onboardings]
  )

  return {
    query: data,
    getOnboarding
  }
}
